import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useOrganization } from '../../context/OrganizationContext';

import {
  ArrowsClockwise,
  CaretRight,
  ChartBar,
  CheckCircle,
  CircleNotch,
  Clock,
  Pulse,
  Robot,
  SquaresFour,
  TrendUp
} from '@phosphor-icons/react';
export default function DashboardsList() {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboards();
  }, [activeOrganization]);

  const fetchDashboards = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/dashboards`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-organization-id': activeOrganization?.organization_id || ''
        }
      });

      if (!response.ok) throw new Error('Failed to fetch dashboards');

      const data = await response.json();
      setDashboards(data.dashboards || []);
    } catch (err) {
      console.error('Error fetching dashboards:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <div className="text-center">
          <CircleNotch size={48} className="text-[#6366F1] animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading dashboards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161B26] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <SquaresFour className="text-[#6366F1]" size={32} />
              Agent Dashboards
            </h1>
            <p className="text-[#A0AEC0] mt-2">
              Monitor activity and metrics for your AI agents
            </p>
          </div>
          
          <button
            onClick={fetchDashboards}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          >
            <ArrowsClockwise size={18} />
            ArrowsClockwise
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
            {error}
          </div>
        )}

        {/* Dashboards Grid */}
        {dashboards.length === 0 ? (
          <div className="text-center py-16">
            <ChartBar size={64} className="text-[#6366F1]/30 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No Dashboards Yet</h2>
            <p className="text-[#A0AEC0] mb-6">
              Dashboards are created automatically when you create agents.<br />
              Create your first agent to see its dashboard here.
            </p>
            <button
              onClick={() => navigate('/partner-pro/workspace/my-agents')}
              className="px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-medium"
            >
              View My Agents
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dashboards.map((dashboard, index) => (
              <motion.div
                key={dashboard.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate(`/agents/${dashboard.agentId}/dashboard`)}
                className="glass rounded-2xl p-6 cursor-pointer hover:border-[#6366F1]/50 transition-all group"
              >
                {/* SquaresFour Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                      <Robot size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold group-hover:text-[#6366F1] transition-colors">
                        {dashboard.agentName || dashboard.title}
                      </h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        dashboard.agentStatus === 'active' 
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {dashboard.agentStatus || 'Unknown'}
                      </span>
                    </div>
                  </div>
                  <CaretRight size={20} className="text-[#A0AEC0] group-hover:text-[#6366F1] transition-colors" />
                </div>

                {/* KPI Summary */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-3">
                    <div className="flex items-center gap-2 text-[#A0AEC0] text-xs mb-1">
                      <Pulse size={14} />
                      Runs (7d)
                    </div>
                    <p className="text-white font-semibold text-lg">
                      {dashboard.kpis?.runs7d || 0}
                    </p>
                  </div>
                  <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-3">
                    <div className="flex items-center gap-2 text-[#A0AEC0] text-xs mb-1">
                      <CheckCircle size={14} />
                      Success
                    </div>
                    <p className="text-white font-semibold text-lg">
                      {dashboard.kpis?.successRate || 0}%
                    </p>
                  </div>
                </div>

                {/* Last Updated */}
                <div className="flex items-center gap-2 text-[#A0AEC0] text-xs">
                  <Clock size={14} />
                  Last activity: {formatDate(dashboard.lastUpdatedAt)}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
